import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { scrollToSectionWhenReady } from '@/lib/scrollToSection';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Events', href: '/events' },
  { label: 'About Us', href: { pathname: '/', hash: '#about' } },
  { label: 'Gallery', href: '/gallery' },
  { label: 'Podcast and Videos', href: '/podcast-interviews' },
  { label: 'Reviews', href: '/reviews' },
  { label: 'Contact', href: '/contact' },
];

function handleHashNavClick(event, href, pathname, navigate) {
  if (typeof href !== 'object' || !href.hash) return;

  const targetPath = href.pathname ?? '/';
  const sectionId = href.hash.slice(1);

  if (pathname !== targetPath) return;

  event.preventDefault();
  navigate({ pathname: targetPath, hash: href.hash });
  scrollToSectionWhenReady(sectionId, { maxAttempts: 40, intervalMs: 50 });
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const isActive = (href) => {
    if (typeof href === 'object') return false;
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-white ${
          scrolled ? 'shadow-md shadow-black/10' : ''
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 lg:flex items-center lg:justify-between h-16 lg:h-20">
            <Link
              to="/"
              className="flex items-center gap-2.5 group col-start-2 justify-center lg:col-auto lg:justify-start"
            >
              <AppLogo size={120} />
            </Link>

            <nav className="hidden lg:flex items-center gap-5" aria-label="Main navigation">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  onClick={(event) => handleHashNavClick(event, link.href, pathname, navigate)}
                  className={`nav-link-hover text-sm font-medium tracking-wide ${
                    isActive(link.href)
                      ? 'text-primary active'
                      : 'text-foreground/80 hover:text-foreground'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-3 col-start-3 justify-end lg:col-auto">
              <Link
                to="/events"
                className="hidden sm:flex btn-gold items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold tracking-widest uppercase"
              >
                <span>Get Tickets</span>
              </Link>

              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="lg:hidden flex items-center justify-center w-10 h-10 rounded-full border border-border/50 text-foreground hover:border-primary transition-colors"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
              >
                <Icon name={menuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={20} variant="outline" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div
        className={`fixed inset-0 z-40 transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          menuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ background: 'var(--background)' }}
      >
        <div className="flex flex-col h-full pt-24 px-6 pb-12 overflow-y-auto">
          <nav className="flex flex-col gap-1" aria-label="Mobile navigation">
            {navLinks.map((link, i) => (
              <Link
                key={link.label}
                to={link.href}
                onClick={(event) => {
                  setMenuOpen(false);
                  handleHashNavClick(event, link.href, pathname, navigate);
                }}
                className={`flex items-center justify-between py-4 border-b border-border/40 font-display text-3xl font-medium transition-colors ${
                  isActive(link.href) ? 'text-primary' : 'text-foreground hover:text-primary'
                }`}
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <span>{link.label}</span>
                <Icon name="ArrowRightIcon" size={20} className="text-primary opacity-60" />
              </Link>
            ))}
          </nav>

          <div className="mt-auto pt-8">
            <Link
              to="/events"
              onClick={() => setMenuOpen(false)}
              className="btn-gold flex items-center justify-center gap-2 w-full py-4 rounded-full text-sm font-bold tracking-widest uppercase"
            >
              <span>Get Tickets</span>
              <Icon name="TicketIcon" size={16} />
            </Link>
          </div>

          <div className="flex items-center gap-5 mt-8 pt-6 border-t border-border/40">
            {[
              { name: 'Facebook', icon: 'GlobeAltIcon' },
              { name: 'Instagram', icon: 'CameraIcon' },
              { name: 'YouTube', icon: 'PlayCircleIcon' },
              { name: 'TikTok', icon: 'MusicalNoteIcon' },
            ].map((social) => (
              <button
                key={social.name}
                aria-label={social.name}
                className="social-icon-hover text-muted-foreground"
              >
                <Icon name={social.icon} size={22} variant="outline" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
